import { Box, Select } from '@mantine/core'
import { useForm } from '@mantine/form'
import React from 'react'

import { useAppSelector } from 'src/v2/store/hooks'

import { ChangeOrganizationParams } from '../../hooks/useChangeOrganizationProcess.hook'
import { getDefaultMembershipId } from '../../utils/organizationMembership.utils'
import styles from './OrganizationSelector.module.scss'

import {
  selectCurrentOrganization,
  selectOrganizations,
} from '@v2/domains/user/selectors/user.selectors'

interface OrganizationSelectorProps {
  testIdFeature?: string
  onChange: (params: ChangeOrganizationParams) => void
  disabled?: boolean
}

export const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  testIdFeature,
  onChange,
  disabled = false,
}) => {
  const currentOrganization = useAppSelector(selectCurrentOrganization)
  const organizationsByUser = useAppSelector(selectOrganizations)

  const currentOrganizationId = currentOrganization.id

  const form = useForm({
    initialValues: {
      organizationId: currentOrganizationId || '',
    },
  })

  const handleChange = (value: string | null) => {
    if (value) {
      const organization = organizationsByUser.find((org) => org.id === value)

      if (organization) {
        const organizationId = organization.id!
        const membershipId = getDefaultMembershipId(organization)
        onChange({
          organizationId,
          membershipId,
          source: 'manual',
        })
        form.setFieldValue('organizationId', value)
      }
    }
  }

  const data = organizationsByUser.map((org) => ({
    value: org.id,
    label: org.name,
  }))

  return (
    <Box className={styles.container}>
      <Select
        data-testid={testIdFeature ? `${testIdFeature}-select` : undefined}
        label="Organization"
        placeholder="Select an organization"
        data={data}
        renderOption={
          testIdFeature
            ? ({ option }) => (
                <span
                  data-testid={`${testIdFeature}-record-option-${option.value}`}
                >
                  {option.label}
                </span>
              )
            : undefined
        }
        value={form.values.organizationId}
        onChange={handleChange}
        withAsterisk
        disabled={disabled}
        error={form.errors.organizationId}
        className={styles.selector}
        size="md"
      />
    </Box>
  )
}
